// Profile page component with Posts/Blog tabs

import { useParams, useNavigate, Navigate } from 'react-router'
import { useProfile } from '@/hooks/useProfile'
import { useUserPosts, getAllUserPosts } from '@/hooks/useUserPosts'
import { useAuthorPosts, getAllAuthorPosts } from '@/hooks/useAuthorPosts'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useAuthStore } from '@/stores/authStore'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PostCard } from '@/components/feed/PostCard'
import { PostSkeleton } from '@/components/feed/PostSkeleton'
import { formatNumber } from '@/utils/formatters'
import { ArrowLeft, MapPin, Link as LinkIcon, Calendar, RefreshCw } from 'lucide-react'

export default function ProfilePage() {
  const { atUsername } = useParams<{ atUsername: string }>()
  const navigate = useNavigate()

  // Extract username from @username format
  const username = atUsername?.startsWith('@') ? atUsername.slice(1) : ''

  const currentUser = useAuthStore((state) => state.user)
  const isOwnProfile = currentUser?.username === username

  const { data: profile, isLoading: profileLoading, isError: profileError } = useProfile(username)

  // Posts tab - author's own posts only (using getPostsByAuthor API)
  const {
    data: authorPostsData,
    isLoading: authorPostsLoading,
    isError: authorPostsError,
    fetchNextPage: fetchNextAuthorPosts,
    hasNextPage: hasNextAuthorPosts,
    isFetchingNextPage: isFetchingNextAuthorPosts,
    refetch: refetchAuthorPosts,
  } = useAuthorPosts({
    username: username || '',
    enabled: !!username,
  })

  // Blog tab - all activity including resteems (using getAccountFeed API)
  const {
    data: blogPostsData,
    isLoading: blogPostsLoading,
    isError: blogPostsError,
    fetchNextPage: fetchNextBlogPosts,
    hasNextPage: hasNextBlogPosts,
    isFetchingNextPage: isFetchingNextBlogPosts,
    refetch: refetchBlogPosts,
  } = useUserPosts({
    username: username || '',
    enabled: !!username,
  })

  // Infinite scroll refs for each tab
  const postsLoadMoreRef = useInfiniteScroll({
    hasNextPage: hasNextAuthorPosts,
    isFetchingNextPage: isFetchingNextAuthorPosts,
    fetchNextPage: fetchNextAuthorPosts,
  })

  const blogLoadMoreRef = useInfiniteScroll({
    hasNextPage: hasNextBlogPosts,
    isFetchingNextPage: isFetchingNextBlogPosts,
    fetchNextPage: fetchNextBlogPosts,
  })

  const authorPosts = getAllAuthorPosts(authorPostsData)
  const blogPosts = getAllUserPosts(blogPostsData)

  // Redirect if URL doesn't start with @ (invalid profile URL)
  if (!atUsername?.startsWith('@')) {
    return <Navigate to="/" replace />
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onBack={() => navigate(-1)} />
        <main className="container max-w-2xl px-4 py-8">
          <ProfileSkeleton />
        </main>
      </div>
    )
  }

  if (profileError || !profile) {
    return (
      <div className="min-h-screen bg-background">
        <Header onBack={() => navigate(-1)} />
        <main className="container max-w-2xl px-4 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-lg text-muted-foreground">
                User not found
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onBack={() => navigate(-1)} title={profile.username} />

      <main className="container max-w-2xl px-4 py-8">
        {/* Profile Card */}
        <Card className="mb-8">
          <CardContent className="p-6">
            {/* Profile Header */}
            <div className="flex items-start gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage
                  src={`https://steemitimages.com/u/${profile.username}/avatar`}
                  alt={profile.username}
                />
                <AvatarFallback className="text-2xl">
                  {profile.username[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{profile.username}</h2>
                    <p className="text-sm text-muted-foreground">
                      Reputation: {profile.reputation}
                    </p>
                  </div>
                  {isOwnProfile && (
                    <Button variant="outline" size="sm" disabled>
                      Edit Profile
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* About */}
            {profile.about && (
              <p className="mt-4 text-sm">{profile.about}</p>
            )}

            {/* Meta Info */}
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
              {profile.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {profile.location}
                </span>
              )}
              {profile.website && (
                <a
                  href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-foreground"
                >
                  <LinkIcon className="h-4 w-4" />
                  {profile.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Joined {profile.created.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
              </span>
            </div>

            {/* Stats */}
            <div className="mt-6 flex gap-6 border-t pt-4">
              <div className="text-center">
                <div className="text-lg font-bold">{formatNumber(profile.postCount)}</div>
                <div className="text-xs text-muted-foreground">Posts</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold">{formatNumber(profile.followerCount)}</div>
                <div className="text-xs text-muted-foreground">Followers</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold">{formatNumber(profile.followingCount)}</div>
                <div className="text-xs text-muted-foreground">Following</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Posts Section with Tabs */}
        <Tabs defaultValue="posts" className="w-full">
          <TabsList className="mb-4 grid w-full grid-cols-2">
            <TabsTrigger value="posts">Posts</TabsTrigger>
            <TabsTrigger value="blog">Blog</TabsTrigger>
          </TabsList>

          {/* Posts Tab - Author's own posts only */}
          <TabsContent value="posts">
            {authorPostsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <PostSkeleton key={i} />
                ))}
              </div>
            ) : authorPostsError ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="mb-4 text-muted-foreground">게시글을 불러오는데 실패했습니다</p>
                  <Button variant="outline" size="sm" onClick={() => refetchAuthorPosts()}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    다시 시도
                  </Button>
                </CardContent>
              </Card>
            ) : authorPosts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-muted-foreground">작성한 게시글이 없습니다</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {authorPosts.map((post) => (
                  <PostCard key={`posts-${post.author}-${post.permlink}`} post={post} />
                ))}

                <div ref={postsLoadMoreRef} className="py-4">
                  {isFetchingNextAuthorPosts && <PostSkeleton />}
                  {!hasNextAuthorPosts && authorPosts.length > 0 && (
                    <p className="text-center text-sm text-muted-foreground">
                      더 이상 게시글이 없습니다
                    </p>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Blog Tab - All activity including resteems */}
          <TabsContent value="blog">
            {blogPostsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <PostSkeleton key={i} />
                ))}
              </div>
            ) : blogPostsError ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="mb-4 text-muted-foreground">게시글을 불러오는데 실패했습니다</p>
                  <Button variant="outline" size="sm" onClick={() => refetchBlogPosts()}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    다시 시도
                  </Button>
                </CardContent>
              </Card>
            ) : blogPosts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-muted-foreground">게시글이 없습니다</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {blogPosts.map((post) => (
                  <PostCard key={`blog-${post.author}-${post.permlink}`} post={post} />
                ))}

                <div ref={blogLoadMoreRef} className="py-4">
                  {isFetchingNextBlogPosts && <PostSkeleton />}
                  {!hasNextBlogPosts && blogPosts.length > 0 && (
                    <p className="text-center text-sm text-muted-foreground">
                      더 이상 게시글이 없습니다
                    </p>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  )
}

function Header({ onBack, title }: { onBack: () => void; title?: string }) {
  return (
    <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center gap-4 px-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">{title || 'Profile'}</h1>
      </div>
    </header>
  )
}

function ProfileSkeleton() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <Skeleton className="h-20 w-20 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <Skeleton className="mt-4 h-16 w-full" />
        <div className="mt-6 flex gap-6 border-t pt-4">
          <Skeleton className="h-10 w-16" />
          <Skeleton className="h-10 w-16" />
          <Skeleton className="h-10 w-16" />
        </div>
      </CardContent>
    </Card>
  )
}
